import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Move, Lock, Save, Check, Settings, X, MapPin } from 'lucide-react';
import MapHotspot from './MapHotspot';
import AreaDetailDialog from './AreaDetailDialog';
import PinchZoomWrapper from './PinchZoomWrapper';
import { RESORT_MAP_URL, HOTEL_PLAN_URL, FLOOR_PLAN_EXTENDED_URL, BACK_OF_HOUSE_URL } from '../data/resortAreas';
import { isAdminLike } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual feedback for project-search "open widget" requests. The
// hotspot pulses for HIGHLIGHT_AFTER_CLOSE_MS after the dialog closes
// so the visitor remembers WHERE on the map they were just looking.
const HIGHLIGHT_CLASS = 'search-hit-highlight';
const HIGHLIGHT_AFTER_CLOSE_MS = 5000;

// Fallback images for slides that don't have an uploaded image
const FALLBACK_IMAGES = {
  'back-of-house': BACK_OF_HOUSE_URL,
  'indoor': HOTEL_PLAN_URL,
  'rooftop': FLOOR_PLAN_EXTENDED_URL,
  'outdoor': RESORT_MAP_URL,
};

export default function InteractiveMap({ areas, onAreasUpdate, settings, slides, onStartTour }) {
  const [selectedArea, setSelectedArea] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // The area the visitor was just looking at via Project Search — kept
  // in state so the "Last viewed" indicator can persist past the
  // dialog close (until visitor dismisses it or searches again).
  const [lastViewed, setLastViewed] = useState(null);
  const highlightTimeoutRef = useRef(null);
  // Detect the PWA install banner so we can lift the Last-Viewed pill
  // above it (mirrors FreshnessPill.jsx pattern). The banner sits at
  // bottom-center and would otherwise steal pointer events from the
  // pill's Reopen/Dismiss buttons.
  const [installBannerVisible, setInstallBannerVisible] = useState(false);
  // Open-snag counts keyed by area_id — powers the small red badge on
  // each hotspot. Only fetched for signed-in users (the counts endpoint
  // requires auth); public visitors simply see no badges.
  const [snagCounts, setSnagCounts] = useState({});
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/snags/counts`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setSnagCounts(data?.counts || {});
      } catch (_) { /* badges are non-critical — fail silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const check = () => setInstallBannerVisible(!!document.querySelector('[data-testid="install-banner"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  // Apply pulse-glow to a hotspot + smooth-scroll it into view.
  const highlightHotspot = useCallback((areaId, scroll = true) => {
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    const el = document.querySelector(`[data-testid="hotspot-${areaId}"]`);
    if (!el) return;
    el.classList.add(HIGHLIGHT_CLASS);
    if (scroll) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }, []);

  const clearHighlight = useCallback(() => {
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
  }, []);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usersByEmail, setUsersByEmail] = useState({});

  // `isAdmin` is used to reveal the "Admin Panel" CTA + the edit-mode
  // toggle on the map. Per the product rule, only role=admin should see
  // these — editors get viewer-style UI on the public map.
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const role = localStorage.getItem('user_role');
    setIsAdmin(!!token && isAdminLike(role));
  }, []);

  // One-shot lookup of {email -> {name, title, photo_url}} so MapHotspot can
  // render owner avatars without each hotspot making its own request.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/users-public`)
      .then(({ data }) => {
        if (cancelled || !Array.isArray(data)) return;
        const map = {};
        data.forEach((u) => { if (u.email) map[u.email] = u; });
        setUsersByEmail(map);
      })
      .catch(() => { /* okay if list is empty */ });
    return () => { cancelled = true; };
  }, []);

  // Listen for `open-area` custom events fired by Project Search so a
  // search hit can pop the AreaDetailDialog without the user having to
  // hunt down the matching hotspot on the map. We pulse-glow the
  // hotspot and smooth-scroll it into view first, then open the dialog
  // ~600ms later so the visitor SEES where on the map the widget lives.
  useEffect(() => {
    const handler = (e) => {
      const aid = e.detail?.areaId;
      if (!aid) return;
      const a = (areas || []).find((x) => x.id === aid);
      if (!a) return;
      highlightHotspot(aid);
      setLastViewed(a);
      const t = setTimeout(() => {
        setSelectedArea(a);
        setDialogOpen(true);
      }, 600);
      return () => clearTimeout(t);
    };
    window.addEventListener('open-area', handler);
    return () => window.removeEventListener('open-area', handler);
  }, [areas, highlightHotspot]);

  const handleHotspotClick = (area) => {
    setSelectedArea(area);
    setDialogOpen(true);
    // Track regular clicks too so the "Last viewed" indicator works
    // even when the visitor finds the hotspot themselves.
    setLastViewed(area);
  };

  const handleClose = (open) => {
    if (!open) {
      setDialogOpen(false);
      // Keep the hotspot pulse-glowing for a few seconds so the visitor
      // can see WHERE on the map the widget they just opened lives.
      const aid = selectedArea?.id;
      if (aid) {
        highlightHotspot(aid, false);
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          clearHighlight();
          // The pulse fades but the "Last viewed" pill at the bottom
          // sticks around — visitor decides when to dismiss it.
        }, HIGHLIGHT_AFTER_CLOSE_MS);
      }
      setTimeout(() => setSelectedArea(null), 300);
    }
  };

  // Cleanup on unmount.
  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    clearHighlight();
  }, [clearHighlight]);

  const handleReopenLastViewed = () => {
    if (!lastViewed) return;
    highlightHotspot(lastViewed.id);
    setSelectedArea(lastViewed);
    setDialogOpen(true);
  };

  const handleDismissLastViewed = () => {
    clearHighlight();
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setLastViewed(null);
  };

  const handlePositionChange = useCallback((areaId, x, y) => {
    setPendingChanges((prev) => ({ ...prev, [areaId]: { x, y } }));
    setSaved(false);
    if (onAreasUpdate) {
      onAreasUpdate((prev) =>
        prev.map((a) => (a.id === areaId ? { ...a, position_x: x, position_y: y } : a))
      );
    }
  }, [onAreasUpdate]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const entries = Object.entries(pendingChanges);
      for (const [areaId, pos] of entries) {
        await axios.patch(`${API}/areas/${areaId}/position`, {
          position_x: pos.x,
          position_y: pos.y,
        }, { headers, withCredentials: true });
      }
      setPendingChanges({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const changeCount = Object.keys(pendingChanges).length;
  const accentColor = settings?.accent_color || '#FF6F59';
  const primaryColor = settings?.primary_color || '#1F4FBE';
  const mapTitle = settings?.map_section_title || 'Every dot, a live project.';
  const mapSubtitle = settings?.map_section_subtitle || 'Click any highlighted area for before-and-after visuals, scope, status, and the investment story behind it.';

  return (
    <section id="map-section" data-testid="map-section" className="py-16 md:py-24 px-4 md:px-8 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: accentColor }}>
            Interactive Reconstruction Map
          </p>
          <h2 className="text-3xl md:text-4xl tracking-tight leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
            {mapTitle}
          </h2>
          <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif', color: '#4A5D5D' }}>
            {mapSubtitle}
          </p>
        </div>

        {/* Edit Mode Toggle */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
          {/* Legend - Color Coding */}
          <div data-testid="map-legend" className="flex flex-wrap items-center gap-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: '#22C55E' }} />
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>Reconstruction to Pre-Hurricane State</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: '#EAB308' }} />
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>Crucial Enhancements</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>Requested Enhancements</span>
            </div>
          </div>

          <span className="text-xs" style={{ color: '#A3D4D4' }}>|</span>

          {/* Legend - Completion Level */}
          <div className="flex flex-wrap items-center gap-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#6B7280' }}>?</span>
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>Planned</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#6B7280' }}>~</span>
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>In Progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#6B7280' }}>{'\u2713'}</span>
              <span className="text-xs font-semibold" style={{ color: '#4A5D5D' }}>Completed</span>
            </div>
          </div>

          <span className="text-xs" style={{ color: '#A3D4D4' }}>|</span>

          {isAdmin && (
            <button
              data-testid="edit-mode-toggle"
              onClick={async () => {
                if (editMode) {
                  if (Object.keys(pendingChanges).length > 0) await handleSaveAll();
                  setEditMode(false);
                } else {
                  setEditMode(true);
                }
              }}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all disabled:opacity-50"
              style={{ backgroundColor: editMode ? '#FFD700' : '#EAE5D9', color: editMode ? '#1A2E2E' : '#4A5D5D', fontFamily: 'Manrope, sans-serif' }}
            >
              {editMode ? <Lock className="w-3.5 h-3.5" /> : <Move className="w-3.5 h-3.5" />}
              {editMode ? (saving ? 'Saving & Locking...' : 'Lock Widgets') : 'Move Widgets'}
            </button>
          )}

          {editMode && changeCount > 0 && (
            <button data-testid="save-positions-btn" onClick={handleSaveAll} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50" style={{ backgroundColor: primaryColor, fontFamily: 'Manrope, sans-serif' }}>
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving...' : `Save ${changeCount} change${changeCount > 1 ? 's' : ''}`}
            </button>
          )}

          {saved && (
            <span className="flex items-center gap-1 text-xs font-bold" style={{ color: primaryColor }}>
              <Check className="w-3.5 h-3.5" /> Saved!
            </span>
          )}

          {isAdmin && (
            <>
              <span className="text-xs" style={{ color: '#A3D4D4' }}>|</span>
              <a href="/admin/dashboard" data-testid="admin-panel-link" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5" style={{ backgroundColor: '#1A2E2E', color: '#F4F1EB', fontFamily: 'Manrope, sans-serif' }}>
                <Settings className="w-3.5 h-3.5" /> Admin Panel
              </a>
            </>
          )}
        </div>

        {editMode && (
          <div className="text-center mb-4 py-2 px-4 rounded-lg text-xs font-bold" style={{ backgroundColor: '#FFD70020', color: '#8B6914', fontFamily: 'Manrope, sans-serif' }}>
            Drag widgets to reposition them. Names and coordinates shown on each widget. Positions auto-save when you click "Lock Widgets".
          </div>
        )}

        {/* Dynamic Slides */}
        {slides && slides.length > 0 ? (
          slides.map((slide, idx) => {
            const slideAreas = areas.filter((a) => a.zone === slide.zone);
            const imgUrl = slide.image_url || FALLBACK_IMAGES[slide.zone] || '';
            const isIndoor = slide.zone === 'indoor';

            return (
              <div
                key={slide.id}
                data-testid={`slide-${slide.zone}-${idx}`}
                className={`relative w-full rounded-2xl overflow-hidden shadow-xl border ${idx < slides.length - 1 ? 'mb-10' : ''}`}
                style={{ backgroundColor: '#FFFFFF', borderColor: editMode ? '#FFD700' : 'rgba(0, 61, 165, 0.1)' }}
              >
                <div className="absolute top-4 left-4 z-20 map-label px-4 py-2 rounded-full">
                  <span className="text-xs font-bold tracking-wide" style={{ color: '#003DA5', fontFamily: 'Manrope, sans-serif' }}>
                    {slide.name}
                  </span>
                </div>
                {/* Image + hotspots are wrapped in a single transform so they
                    pinch-zoom together on touch devices. Edit-mode bypasses
                    the gestures so admin drag-to-reposition still works. */}
                <PinchZoomWrapper disabled={editMode} testId={`pinch-${slide.zone}-${idx}`}>
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt={slide.name}
                      className="w-full h-auto block"
                      style={isIndoor ? { transform: 'scale(1.1)', transformOrigin: 'center center' } : undefined}
                    />
                  )}
                  {slideAreas.map((area) => (
                    <MapHotspot
                      key={area.id}
                      area={area}
                      onClick={handleHotspotClick}
                      editMode={editMode}
                      onPositionChange={handlePositionChange}
                      usersByEmail={usersByEmail}
                      openSnags={snagCounts[area.id] || 0}
                    />
                  ))}
                </PinchZoomWrapper>
              </div>
            );
          })
        ) : (
          /* Fallback: render hardcoded slides if no slides from DB */
          <p className="text-center text-sm" style={{ color: '#7A8C8C' }}>No slides configured.</p>
        )}

        <p className="text-center mt-6 text-xs" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A8C8C' }}>
          {editMode
            ? 'Drag markers to reposition. Click "Lock Widgets" when done to save.'
            : 'Hover over markers to see area names. Click to view reconstruction details.'}
        </p>
      </div>

      <AreaDetailDialog area={selectedArea} open={dialogOpen} onClose={handleClose} onStartTour={onStartTour} />

      {/* "Last viewed" floating pill — bottom-center. Appears after the
          visitor opens any widget (whether from search or direct click)
          so they always remember WHERE on the map they were just
          looking, and can re-open with one tap. */}
      {lastViewed && !dialogOpen && (
        <div
          data-testid="last-viewed-indicator"
          className="fixed left-1/2 -translate-x-1/2 z-[10010] flex items-center gap-2 px-4 py-2 rounded-full shadow-xl"
          style={{
            bottom: installBannerVisible ? '6.5rem' : '1.5rem',
            backgroundColor: 'white',
            border: '1px solid #E2DBC8',
            fontFamily: 'Manrope',
            boxShadow: '0 8px 24px rgba(0,61,165,0.18)',
            transition: 'bottom 240ms ease',
          }}
        >
          <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#003DA5' }} />
          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#7A8C8C' }}>Last viewed</span>
          <button
            data-testid="last-viewed-reopen"
            onClick={handleReopenLastViewed}
            className="text-sm font-bold underline hover:no-underline truncate max-w-[200px]"
            style={{ color: '#003DA5' }}
            title="Reopen this widget"
          >
            {lastViewed.name}
          </button>
          <button
            data-testid="last-viewed-dismiss"
            onClick={handleDismissLastViewed}
            className="p-1 rounded-full ml-1 hover:bg-slate-100"
            aria-label="Dismiss last-viewed indicator"
          >
            <X className="w-3.5 h-3.5" style={{ color: '#7A8C8C' }} />
          </button>
        </div>
      )}

      {/* Pulse-glow animation used by `.search-hit-highlight` — applied
          to hotspot elements via DOM imperative .classList so we don't
          have to thread state into every MapHotspot. */}
      <style>{`
        .${HIGHLIGHT_CLASS} {
          animation: search-hit-pulse 1.4s ease-in-out infinite;
          z-index: 55 !important;
        }
        .${HIGHLIGHT_CLASS} > span:first-child {
          background-color: rgba(0, 61, 165, 0.42) !important;
        }
        @keyframes search-hit-pulse {
          0%   { filter: drop-shadow(0 0 0 rgba(0,61,165,0.0));   transform: translate(-50%, -50%) scale(1); }
          50%  { filter: drop-shadow(0 0 18px rgba(0,61,165,0.7)); transform: translate(-50%, -50%) scale(1.28); }
          100% { filter: drop-shadow(0 0 0 rgba(0,61,165,0.0));   transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </section>
  );
}
